<template lang="pug">
  v-dialog(v-model="showDialog" persistent max-width="560px" :fullscreen="$vuetify.breakpoint.xsOnly")
    v-card
      v-card-title {{ $t('boarderEmailReminderDialog.title') }}
      v-card-text.pt-6
        p {{ $t('boarderEmailReminderDialog.message') }}
        v-form(ref="form" v-model="isFormValid" @submit.prevent="onSaveClick")
          v-text-field(
            v-model="email"
            :label="$t('boarderEmailReminderDialog.emailLabel')"
            :rules="emailRules"
            outlined
            autofocus
            autocomplete="email"
          )
      v-card-actions
        v-spacer
        v-btn(text :disabled="isSaving" @click="onPostponeClick") {{ $t('boarderEmailReminderDialog.postponeButtonLabel') }}
        v-btn(color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSaveClick") {{ $t('boarderEmailReminderDialog.saveButtonLabel') }}
</template>

<script lang="ts">
import Vue from 'vue'
import {Action, Getter} from 'vuex-class'
import {CurrentUser} from '@/model/CurrentUser'
import {USER_LOGGED_IN} from '@/store/getter-types'
import {FETCH_ACCOUNT_EMAILS, UPDATE_ACCOUNT_EMAILS} from '@/store/action-types'
import {AccountEmail} from '@/model/AccountEmail'
import {UpdateAccountEmail} from '@/types/payload'
import {TranslateResult} from 'vue-i18n'
import {validateEmail, valueIsRequired} from '@/services/validation'
import {usePiniaStore} from '@/store/piniaStore'

export default Vue.extend({
  name: 'BoarderEmailReminderDialog',

  data() {
    return {
      showDialog: false,
      email: '',
      isFormValid: false,
      isSaving: false,
      piniaStore: usePiniaStore(),
      emailRules: [
        valueIsRequired,
        validateEmail,
      ] as Array<(v: string) => string | boolean | TranslateResult>
    }
  },

  computed: {
    currentUser(): CurrentUser | undefined {
      return this.$store.getters[USER_LOGGED_IN]
    },

    reminderGateKey(): string {
      const userId = this.currentUser?.id ?? 'none'

      return `${userId}:${this.piniaStore.profileDialogOpen ? 1 : 0}:${this.piniaStore.emailReminderVersion}`
    }
  },

  watch: {
    reminderGateKey: {
      immediate: true,
      handler(): void {
        void this.evaluateReminder()
      }
    }
  },

  methods: {
    async evaluateReminder(): Promise<void> {
      const user = this.currentUser

      if (!user || this.piniaStore.profileDialogOpen) {
        this.showDialog = false
        return
      }

      if (this.piniaStore.isEmailReminderSnoozedForUser(user.id)) {
        this.showDialog = false
        return
      }

      try {
        const emails = await this.fetchAccountEmails()

        if (this.hasUsableEmail(emails)) {
          this.piniaStore.clearEmailReminderSnoozeForUser(user.id)
          this.showDialog = false
          return
        }

        this.email = this.getPrefilledEmail(emails)
        this.showDialog = true
      } catch (e) {
        this.showDialog = false
      }
    },

    hasUsableEmail(emails: AccountEmail[]): boolean {
      return emails.some((item: AccountEmail) => this.piniaStore.isUsableEmail(item.email))
    },

    getPrefilledEmail(emails: AccountEmail[]): string {
      return emails[0]?.email ?? ''
    },

    async fetchAccountEmails(): Promise<AccountEmail[]> {
      return this.$store.dispatch(FETCH_ACCOUNT_EMAILS)
    },

    async onSaveClick(): Promise<void> {
      const form = this.$refs.form as Vue & { validate: () => boolean }
      const isValid = form.validate()

      if (!isValid) {
        return
      }

      this.isSaving = true

      try {
        const payload: UpdateAccountEmail[] = [{ email: this.email.trim() }]
        await this.$store.dispatch(UPDATE_ACCOUNT_EMAILS, payload)
        if (this.currentUser) {
          this.piniaStore.clearEmailReminderSnoozeForUser(this.currentUser.id)
        }
        this.piniaStore.recheckEmailReminder()
        this.showDialog = false
      } finally {
        this.isSaving = false
      }
    },

    onPostponeClick(): void {
      const user = this.currentUser

      if (user) {
        this.piniaStore.snoozeEmailReminderForUser(user.id)
      }

      this.showDialog = false
    }
  }
})
</script>
